import { subscribe } from "~/js/common/pubsub";
import { PUB_SUB_EVENTS } from "~/js/common/constants";

if (!customElements.get("recipient-form")) {
  customElements.define(
    "recipient-form",
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(
          `#Recipient-fields-live-region-${this.dataset.sectionId}`,
        );
        this.checkboxInput = this.querySelector(
          `#Recipient-checkbox-${this.dataset.sectionId}`,
        );
        this.checkboxInput.disabled = false;
        this.recipientFieldsContainer = this.querySelector(".recipient-fields");
        this.hiddenControlField = this.querySelector(
          `#Recipient-control-${this.dataset.sectionId}`,
        );
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(
          `#Recipient-email-${this.dataset.sectionId}`,
        );
        this.nameInput = this.querySelector(
          `#Recipient-name-${this.dataset.sectionId}`,
        );
        this.messageInput = this.querySelector(
          `#Recipient-message-${this.dataset.sectionId}`,
        );
        this.sendonInput = this.querySelector(
          `#Recipient-send-on-${this.dataset.sectionId}`,
        );
        this.offsetProperty = this.querySelector(
          `#Recipient-timezone-offset-${this.dataset.sectionId}`,
        );
        if (this.offsetProperty)
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.preventSendOnPasteOrDrop = (event) => event.preventDefault();
        this.clearSendOnCustomValidity = () => {
          if (this.sendonInput) this.sendonInput.setCustomValidity("");
        };
        this.validateSendOnBeforeSubmit =
          this.validateSendOnBeforeSubmit.bind(this);
        this.blockSendOnDirectTyping =
          this.blockSendOnDirectTyping.bind(this);
        this.sanitizeSendOnField = this.sanitizeSendOnField.bind(this);
        this.formEl = null;
        this._sendOnValidationAttached = false;

        this.errorMessageWrapper = this.querySelector(
          ".product-form__recipient-error-message-wrapper",
        );
        this.errorMessageList = this.errorMessageWrapper?.querySelector("ul");
        this.errorMessage =
          this.errorMessageWrapper?.querySelector(".error-message");
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.cartUpdateUnsubscriber = undefined;
        this.variantChangeUnsubscriber = undefined;
        this.cartErrorUnsubscriber = undefined;
        this.addEventListener("change", this.onChange.bind(this));
        this.onChange();
      }

      connectedCallback() {
        this.attachSendOnValidation();

        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (
              event.source === "product-form" &&
              event.productVariantId.toString() === this.currentProductVariantId
            ) {
              this.resetRecipientForm();
            }
          },
        );

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event.data.sectionId === this.dataset.sectionId) {
              this.currentProductVariantId = event.data.variant.id.toString();
            }
          },
        );

        this.cartErrorUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartError,
          (event) => {
            if (
              event.source === "product-form" &&
              event.productVariantId.toString() === this.currentProductVariantId
            ) {
              this.displayErrorMessage(event.message, event.errors);
            }
          },
        );
      }

      disconnectedCallback() {
        this.detachSendOnValidation();

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.recipientFieldsContainer.hidden = false;
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText =
            window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.recipientFieldsContainer.hidden = true;
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText =
            window.accessibilityStrings.recipientFormCollapsed;
        }
      }

      inputFields() {
        return [
          this.emailInput,
          this.nameInput,
          this.messageInput,
          this.sendonInput,
        ];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ""));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === "object") {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(", ")}`;
            const errorMessageElement = this.querySelector(
              `#${errorMessageId}`,
            );
            const errorTextElement =
              errorMessageElement?.querySelector(".error-message");
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(
                this.createErrorListItem(fieldSelector, message),
              );
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove("hidden");

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute("aria-invalid", true);
            inputElement.setAttribute("aria-describedby", errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement("li");
        const a = document.createElement("a");
        a.setAttribute("href", target);
        a.innerText = message;
        li.appendChild(a);
        li.className = "error-message";
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = "";

        this.querySelectorAll(".recipient-fields .form__message").forEach(
          (field) => {
            field.classList.add("hidden");
            const textField = field.querySelector(".error-message");
            if (textField) textField.innerText = "";
          },
        );

        [
          this.emailInput,
          this.messageInput,
          this.nameInput,
          this.sendonInput,
        ].forEach((inputElement) => {
          inputElement.setAttribute("aria-invalid", false);
          inputElement.removeAttribute("aria-describedby");
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }

      attachSendOnValidation() {
        if (this._sendOnValidationAttached) return;
        this.formEl = this.closest("form");
        if (!this.formEl || !this.sendonInput) return;
        this._sendOnValidationAttached = true;
        this.formEl.addEventListener(
          "submit",
          this.validateSendOnBeforeSubmit,
          true,
        );
        this.sendonInput.addEventListener(
          "paste",
          this.preventSendOnPasteOrDrop,
        );
        this.sendonInput.addEventListener(
          "drop",
          this.preventSendOnPasteOrDrop,
        );
        this.sendonInput.addEventListener(
          "keydown",
          this.blockSendOnDirectTyping,
        );
        this.sendonInput.addEventListener("input", this.sanitizeSendOnField);
        this.sendonInput.addEventListener("blur", this.sanitizeSendOnField);
        this.sendonInput.addEventListener(
          "change",
          this.clearSendOnCustomValidity,
        );
      }

      detachSendOnValidation() {
        if (!this._sendOnValidationAttached) return;
        this._sendOnValidationAttached = false;
        if (this.formEl && this.sendonInput) {
          this.formEl.removeEventListener(
            "submit",
            this.validateSendOnBeforeSubmit,
            true,
          );
          this.sendonInput.removeEventListener(
            "paste",
            this.preventSendOnPasteOrDrop,
          );
          this.sendonInput.removeEventListener(
            "drop",
            this.preventSendOnPasteOrDrop,
          );
          this.sendonInput.removeEventListener(
            "keydown",
            this.blockSendOnDirectTyping,
          );
          this.sendonInput.removeEventListener(
            "input",
            this.sanitizeSendOnField,
          );
          this.sendonInput.removeEventListener(
            "blur",
            this.sanitizeSendOnField,
          );
          this.sendonInput.removeEventListener(
            "change",
            this.clearSendOnCustomValidity,
          );
        }
        this.formEl = null;
      }

      /** Stop keyboard entry of digits/slashes; calendar / picker still works. */
      blockSendOnDirectTyping(event) {
        if (!this.sendonInput || this.sendonInput.disabled) return;
        if (event.ctrlKey || event.metaKey || event.altKey) return;

        const { key } = event;
        const allow = new Set([
          "Tab",
          "Escape",
          "Enter",
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Home",
          "End",
          "PageUp",
          "PageDown",
        ]);
        if (allow.has(key)) return;
        if (key === "Backspace" || key === "Delete") return;
        if (key.length === 1) event.preventDefault();
      }

      /**
       * Clear invalid typed text, bad browser parse, or dates outside min/max (e.g. 12/02/2222).
       */
      sanitizeSendOnField() {
        const el = this.sendonInput;
        if (!el || el.disabled) return;
        el.setCustomValidity("");

        if (el.validity.badInput) {
          el.value = "";
          return;
        }

        const v = el.value.trim();
        if (!v) return;

        if (!this.isValidCalendarIsoDate(v)) {
          el.value = "";
          return;
        }

        const { min, max } = el;
        if ((min && v < min) || (max && v > max)) {
          el.value = "";
        }
      }

      isValidCalendarIsoDate(s) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
        const parts = s.split("-").map((n) => parseInt(n, 10));
        const y = parts[0];
        const m = parts[1];
        const d = parts[2];
        if (m < 1 || m > 12 || d < 1 || d > 31) return false;
        const dt = new Date(Date.UTC(y, m - 1, d));
        return (
          dt.getUTCFullYear() === y &&
          dt.getUTCMonth() === m - 1 &&
          dt.getUTCDate() === d
        );
      }

      validateSendOnBeforeSubmit(event) {
        if (!this.sendonInput || this.sendonInput.disabled) return;
        if (!this.checkboxInput.checked) return;

        this.sanitizeSendOnField();

        this.sendonInput.setCustomValidity("");
        const value = this.sendonInput.value.trim();
        if (!value) return;

        const { min, max } = this.sendonInput;
        if (min && value < min) {
          this.sendonInput.setCustomValidity(
            `Choose a date on or after ${min}.`,
          );
        } else if (max && value > max) {
          this.sendonInput.setCustomValidity(
            `Choose a date on or before ${max}.`,
          );
        }

        if (!this.sendonInput.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.sendonInput.reportValidity();
        }
      }
    },
  );
}
